/* eslint-disable-next-line @typescript-eslint/no-restricted-imports -- (https://aka.ms/OWALintWiki)
 * BASELINE. Do not copy and paste!
 *	> 'dexie' import is restricted from being used. Importing from dexie is allowed but needs an additional approver */
import Dexie, { type IndexableType, type Table } from 'dexie';

import type { ReportData } from 'hybridspace-graphql/lib/utils/reportDataType';
import { logGreyError } from 'owa-analytics';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import type { Building, Campus, Features, Place } from 'owa-graph-schema';

export interface UserFeatureAccess {
    ownerId: string;
    featureAccess: Array<Features | null> | undefined;
    timestamp: number;
}

export interface LatestReportDates {
    ownerId: string;
    reportDates: Array<{
        key?: string | null;
        value?: string | null;
    } | null>;
    timestamp: number;
}

export interface BuildingsList {
    ownerId: string;
    buildingsList: string;
    timestamp: number;
}

export interface Buildings {
    ownerId: string;
    buildingId: string;
    building: Building;
    timestamp: number;
}

export interface RoomCount {
    ownerId: string;
    buildingFloorKey: string;
    roomCount: number;
    timestamp: number;
}

export interface DeskpoolCount {
    ownerId: string;
    buildingFloorKey: string;
    deskpoolCount: number;
    timestamp: number;
}

export interface Report {
    ownerId: string;
    reportParamsHashKey: string;
    reportData: ReportData;
    timestamp: number;
}

export interface Campuses {
    ownerId: string;
    campusId: string;
    campus: Campus;
    cityId: string;
    timestamp: number;
}

export interface Floors {
    ownerId: string;
    floorId: string;
    buildingId: string;
    floor: Place;
    timestamp: number;
}

export interface Sections {
    ownerId: string;
    sectionId: string;
    floorId: string;
    section: Place;
    timestamp: number;
}

const INDEXED_DB_NAME = 'PlacesAnalyticsCache';
/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * Baseline. DO NOT COPY AND PASTE!, replace this line with a justification or fix the issue.
 *	> Function Dexie called at module scope is not allowed. This may cause unwanted side effects. */
const db = new Dexie(INDEXED_DB_NAME);

/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function stores called at module scope is not allowed. This may cause unwanted side effects.
 *	> Function version called at module scope is not allowed. This may cause unwanted side effects. */
db.version(1).stores({
    userFeatureAccess: 'ownerId, [ownerId+timestamp]',
    latestReportDates: 'ownerId, [ownerId+timestamp]',
    buildingsList: 'ownerId, [ownerId+timestamp]',
    buildings: '[ownerId+buildingId], ownerId, [ownerId+timestamp]',
    roomCount: '[ownerId+buildingFloorKey], ownerId, [ownerId+timestamp]',
    deskpoolCount: '[ownerId+buildingFloorKey], ownerId, [ownerId+timestamp]',
    report: '[ownerId+reportParamsHashKey], ownerId, [ownerId+timestamp]',
    campuses: '[ownerId+campusId], [ownerId+cityId], ownerId, [ownerId+timestamp]',
    floors: '[ownerId+floorId], [ownerId+buildingId], ownerId, [ownerId+timestamp]',
    sections: '[ownerId+sectionId], [ownerId+floorId], ownerId, [ownerId+timestamp]',
});

export function getUserFeatureAccessTable(): Table<
    UserFeatureAccess,
    IndexableType,
    UserFeatureAccess
> {
    return db.table<UserFeatureAccess>('userFeatureAccess');
}

export function getLatestReportDatesTable(): Table<
    LatestReportDates,
    IndexableType,
    LatestReportDates
> {
    return db.table<LatestReportDates>('latestReportDates');
}

export function getBuildingsListTable(): Table<BuildingsList, IndexableType, BuildingsList> {
    return db.table<BuildingsList>('buildingsList');
}

export function getBuildingsTable(): Table<Buildings, IndexableType, Buildings> {
    return db.table<Buildings>('buildings');
}

export function getRoomCountTable(): Table<RoomCount, IndexableType, RoomCount> {
    return db.table<RoomCount>('roomCount');
}

export function getDeskpoolCountTable(): Table<DeskpoolCount, IndexableType, DeskpoolCount> {
    return db.table<DeskpoolCount>('deskpoolCount');
}

export function getReportTable(): Table<Report, IndexableType, Report> {
    return db.table<Report>('report');
}

export function getCampusesTable(): Table<Campuses, IndexableType, Campuses> {
    return db.table<Campuses>('campuses');
}

export function getFloorsTable(): Table<Floors, IndexableType, Floors> {
    return db.table<Floors>('floors');
}

export function getSectionsTable(): Table<Sections, IndexableType, Sections> {
    return db.table<Sections>('sections');
}

export async function deletePlacesAnalyticsCacheDatabase(): Promise<void> {
    if (await Dexie.exists(INDEXED_DB_NAME)) {
        try {
            if (db.isOpen()) {
                db.close(); // Have to close the database before deleting, otherwise InvalidStateError will be thrown.
            }
        } catch (err) {
            logGreyError(
                'PlacesCloseDatabaseError',
                getModuleContextMailboxInfo(),
                err instanceof Error ? err : undefined,
                {
                    dbName: INDEXED_DB_NAME,
                }
            );
        }
        await Dexie.delete(INDEXED_DB_NAME);
    }
}
